import { Drawer } from 'antd'
import React, { useEffect, useState } from 'react'
import { LoadingOverlayCP } from 'submodules/nerit-framework-ui/common/components/loading/overlay/LoadingOverlayCP'
import { DrawerFooterICP } from 'submodules/nerit-framework-ui/common/components/drawer/inner/DrawerFooterICP'
import { useScreenSize } from 'submodules/nerit-framework-ui/common/responsiveness/use-screen-size/UseScreenSize'
import { IntercomUtils } from 'submodules/nerit-framework-ui/common/components/intercom/inner/IntercomUtils'
import styled from 'styled-components'
import { ThemeFrameworkCommon } from 'submodules/nerit-framework-ui/theme/framework/ThemeFrameworkCommon'

const DRAWER_HEADER_HEIGHT = 55

type _AntPropsTP = {
	title?: string | JSX.Element
	visible?: boolean
	footer?: JSX.Element
	onClose?: () => void
	loading?: boolean
	width?: number | string
	height?: number | string
	closable?: boolean
	maskClosable?: boolean
	placement?: 'left' | 'right' | 'bottom' | 'top'
	showMask?: boolean
}

type _ScpPropsTP = {
	rightPadding?: number
	titleUnderline?: boolean
	footerSpaced?: boolean
	noBodyPadding?: boolean
	bodyBackgroundColor?: string
}

interface IDrawerCPProps extends React.PropsWithChildren<_ScpPropsTP & _AntPropsTP> {}

/**
 * COMPONENTE: Drawer generico.
 */
export function DrawerCP(props: IDrawerCPProps): JSX.Element {
	const [showDrawer, setShowDrawer] = useState<boolean>()
	const screenSize = useScreenSize()

	useEffect(handleOpen, [props.visible])

	function handleOpen(): void {
		if (!!props.visible) IntercomUtils.setIntercomVisibility(false)
		setShowDrawer(!!props.visible)
	}

	function handleClose(): void {
		IntercomUtils.setIntercomVisibility(true)
		if (!!props.onClose) props.onClose()
		else setShowDrawer(false)
	}

	if (!props.visible) return <></>

	const bodyMinHeight = props.bodyBackgroundColor
		? `calc(100vh - ${DRAWER_HEADER_HEIGHT}px)`
		: undefined

	return (
		<WrapperSCP rightPadding={props.rightPadding} noBodyPadding={props.noBodyPadding}>
			<Drawer
				visible={showDrawer}
				placement={props.placement ?? 'right'}
				closable={props.closable ?? true}
				destroyOnClose={true}
				onClose={handleClose}
				maskClosable={props.maskClosable ?? props.closable ?? true}
				mask={props.showMask}
				zIndex={10}
				width={screenSize.smd ? '100%' : props.width ?? 450}
				title={props.title}
				height={props.height}
				style={props.bodyBackgroundColor ? {
					height: '100vh',
					backgroundColor: props.bodyBackgroundColor,
				} : undefined}
				bodyStyle={{
					backgroundColor: props.bodyBackgroundColor ?? ThemeFrameworkCommon.browserDefaultBackgroundLight,
					color: ThemeFrameworkCommon.browserDefaultColorDark,
					padding: props.noBodyPadding ? 0 : undefined,
					marginRight: props.rightPadding ?? undefined,
					minHeight: bodyMinHeight,
				}}
				headerStyle={{
					backgroundColor: ThemeFrameworkCommon.browserDefaultBackgroundLight,
					color: ThemeFrameworkCommon.browserDefaultColorDark,
				}}
			>
				<ContentWrapperSCP bodyBackgroundColor={props.bodyBackgroundColor}>
					<LoadingOverlayCP show={props.loading ?? false} />
					{props.children}
				</ContentWrapperSCP>

				{props.footer && <DrawerFooterICP footerSpaced={props.footerSpaced}>{props.footer}</DrawerFooterICP>}
			</Drawer>
		</WrapperSCP>
	)
}

const WrapperSCP = styled.div<_ScpPropsTP>`
	.ant-drawer-title {
		font-size: 20px;
	}

	.ant-drawer-content {
		height: 100vh;
	}

	.ant-drawer-wrapper-body {
		overflow: hidden !important;
		display: flex;
		flex-direction: column;
	}

	.ant-drawer-header {
		position: relative;
		color: ${ThemeFrameworkCommon.browserDefaultColorDark}A6 !important;
		background: ${ThemeFrameworkCommon.browserDefaultBackgroundLight};
		border-bottom: none;
		border-radius: 4px 4px 0 0;
	}

	.ant-drawer-body {
		margin-right: ${(props) => (props.rightPadding ? props.rightPadding : 0)}px;
		overflow: hidden;
		height: 100%;
		padding: ${(props) => (props.noBodyPadding ? '0' : '0 24px')};
	}

	.anticon {
		color: ${ThemeFrameworkCommon.browserDefaultColorDark};
	}
`

const ContentWrapperSCP = styled.div<{ bodyBackgroundColor?: string }>`
	width: 100%;
	overflow-y: auto;
	overflow-x: hidden;
	min-height: ${(props) => (props.bodyBackgroundColor ? `calc(100vh - ${DRAWER_HEADER_HEIGHT}px)` : '100%')};
	background-color: ${(props) => props.bodyBackgroundColor ?? 'transparent'};
`
